import type { SVGProps } from "react";
import {
  MENUHUNTER_BRAND_ORANGE,
  MENUHUNTER_MARK_FORK_PATHS,
  MENUHUNTER_MARK_KNIFE_PATH,
  MENUHUNTER_MARK_MARKER_PATHS,
  MENUHUNTER_MARK_RING_PATHS,
  MENUHUNTER_MARK_VIEWBOX,
  menuhunterMarkStrokeWidth,
  type MenuHunterMarkVariant,
} from "@menuhunter/ui/brand";

export type MenuHunterMarkProps = {
  size?: number;
  /** Fork / knife / markers. Defaults to brand orange. */
  accentColor?: string;
  /**
   * Outer arcs. When omitted, uses CSS theme classes (navy light / white dark)
   * so SSR + FOUC theme script stay in sync without client-only rendering.
   */
  arcColor?: string;
  /** Legacy mono override — paints arcs + accent when set and arc/accent omitted. */
  color?: string;
  variant?: MenuHunterMarkVariant;
  strokeWidth?: number;
  className?: string;
  title?: string;
} & Omit<SVGProps<SVGSVGElement>, "color" | "strokeWidth">;

/**
 * Canonical MenúHunter 2026 mark for Web — same geometry/tokens as consumer PR #96.
 * Theme-aware arc colours via Tailwind `dark:` when `arcColor` is not overridden.
 */
export function MenuHunterMark({
  size = 28,
  accentColor,
  arcColor,
  color,
  variant = "master",
  strokeWidth: strokeWidthOverride,
  className,
  title = "MenúHunter",
  ...rest
}: MenuHunterMarkProps) {
  const resolvedAccent = accentColor ?? color ?? MENUHUNTER_BRAND_ORANGE;
  const resolvedStroke =
    strokeWidthOverride ?? menuhunterMarkStrokeWidth(variant);
  const { width, height } = MENUHUNTER_MARK_VIEWBOX;

  const themeArcClass =
    arcColor || color
      ? undefined
      : "stroke-[#0F172A] dark:stroke-white";
  const resolvedArc = arcColor ?? color;

  const shared = {
    fill: "none" as const,
    strokeWidth: resolvedStroke,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${width} ${height}`}
      className={className}
      role="img"
      aria-label={title}
      {...rest}
    >
      <title>{title}</title>
      {MENUHUNTER_MARK_RING_PATHS.map((d, index) => (
        <path
          key={`ring-${index}`}
          d={d}
          className={themeArcClass}
          {...(resolvedArc ? { stroke: resolvedArc } : {})}
          {...shared}
        />
      ))}
      {MENUHUNTER_MARK_MARKER_PATHS.map((d, index) => (
        <path key={`marker-${index}`} d={d} stroke={resolvedAccent} {...shared} />
      ))}
      {MENUHUNTER_MARK_FORK_PATHS.map((d, index) => (
        <path key={`fork-${index}`} d={d} stroke={resolvedAccent} {...shared} />
      ))}
      <path d={MENUHUNTER_MARK_KNIFE_PATH} stroke={resolvedAccent} {...shared} />
    </svg>
  );
}
